import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { clearAllCookies } from 'helpers/ConfigHelper';
import Loader from 'components/Loader';
import ForgotPasswordCTA from '../ForgotPasswordCTA';
import Header from '../Header';
import SocialLogin from '../SocialLogin';

import Style from './VerifyAccount.module.scss';

const VerifyAccount = ({
  bubbleState,
  ctaProps,
  facebookGenericError,
  facebookLoginOptionalText,
  facebookLoginText,
  facebookSignupOptionalText,
  facebookSignupText,
  forgotPasswordCTAProps,
  headerProps,
  language,
  onFieldChange,
  onSignin,
  onSocialFacebookLogin,
  showFacebookLogin,
  showVerifyConfirmation,
  signinDropdownIconTheme,
  signinEmail,
  signinEmailError,
  signinGenericError,
  signinMigrationDropdownOpen,
  signinPassword,
  signinPasswordError,
  signinShowPassword,
}) => {
  const {
    ccpa_no_account_text: ccpaNoAccountText,
    profileDetails_verifyButton: verifyText,
    profile_emailInputPlaceholder: emailPlaceholder,
    profile_genericInvalidError: invalidError,
    profile_passwordInputPlaceholder: passwordPlaceholder,
    profile_emailInvalidError: emailInvalidError,
    profile_verifyTermsText: verifyTermsText,
    profile_confirmationMessage: confirmationMessage,
    profile_confirmationHeader: confirmationHeader,
    profileDetails_completeButton: completeButtonText,
    profile_verifyPrivacyPolicyText: verifyPrivacyPolicyText,
    profile_verifyHeaderText: verifyHeaderText,
    profile_verifyPrivacyPolicyLinkText: verifyPrivacyPolicyLinkText,
    profile_verifyTermsBoldText: verifyTermsBoldText,
    profile_verifyTermsLastText: verifyTermsLastText,
    profile_verifyWithoutPasswordText: verifyWithoutPasswordText,
  } = language || {};
  const privacyPolicyUrl = _.get(window, 'ExternalConfig.privacyPolicyUrl', '');
  const { text: CTAText, onClick: CTAOnClick } = ctaProps;
  const { onClose } = headerProps;
  const passwordType = signinShowPassword ? 'text' : 'password';
  let passwordErrorText;
  let emailErrorText;
  let signinPasswordInput = React.createRef();
  let errorText = signinGenericError ? ccpaNoAccountText : null;

  clearAllCookies();

  const [isVerifyLoading, setIsVerifyLoading] = useState(false);
  const [isNoAccountLoading, setIsNoAccountLoading] = useState(false);

  useEffect(() => {
    if (signinEmailError || signinPasswordError || signinGenericError) {
      setIsVerifyLoading(false);
      setIsNoAccountLoading(false);
    }
  }, [signinEmailError, signinPasswordError, signinGenericError]);

  const handleNoAccountClick = () => {
    CTAOnClick();
    setIsNoAccountLoading(true);
  };

  if (_.toLower(signinGenericError) === 'invalid loginid') {
    errorText = invalidError;
  }

  if (signinPasswordError) {
    passwordErrorText = signinPasswordError;
  }

  if (signinEmailError) {
    emailErrorText = emailInvalidError;
  }

  const handleOnChangePasswordInput = e => {
    onFieldChange(e);
    if (signinPasswordInput.current) {
      signinPasswordInput.current.focus();
    }
  };

  return (
    <main className={Style.verify}>
      <Header {...headerProps} />
      <div className={Style.verifyContainer}>
        {showVerifyConfirmation ? (
          <div className={Style.confirmationContainer}>
            <h1 className={Style.verifyHeaderText}>{confirmationHeader}</h1>
            <p className={Style.confirmationMessage}>{confirmationMessage}</p>
            <button
              className={Style.btnWhite}
              onClick={() => onClose({ goHome: true, isCCPAVerify: true })}
            >
              {completeButtonText}
            </button>
          </div>
        ) : (
          <>
            <h1 className={Style.verifyHeaderText}>{verifyHeaderText}</h1>
            <p className={Style.termsText}>
              {verifyTermsText}
              <strong> {verifyTermsBoldText} </strong>
              {verifyTermsLastText}
            </p>
            <p className={Style.termsNoPasswordText}>{verifyWithoutPasswordText}</p>
            <div className={Style.error}>{errorText}</div>
            <form
              className={Style.verifyForm}
              action=""
              method="post"
              onSubmit={e => {
                e.preventDefault();
                onSignin({ isCCPAVerify: true });
                setIsVerifyLoading(true);
                return false;
              }}
            >
              <div className={Style.signupFormInputContainer}>
                <div className={Style.signInPasswordContainer}>
                  <input
                    className={Style.inputField}
                    autoFocus
                    type="email"
                    name="signinEmail"
                    placeholder={emailPlaceholder}
                    value={signinEmail}
                    onChange={onFieldChange}
                    autoCapitalize="none"
                  />
                  <div className={Style.error}>{emailErrorText}</div>
                </div>
                <div className={Style.signInPasswordContainer}>
                  <input
                    className={Style.inputField}
                    type={passwordType}
                    name="signinPassword"
                    placeholder={passwordPlaceholder}
                    value={signinPassword}
                    onChange={onFieldChange}
                    ref={input => {
                      signinPasswordInput = input;
                    }}
                    autoCapitalize="none"
                    autoComplete="off"
                    autoCorrect="off"
                    spellCheck={false}
                  />
                  <label className={Style.passwordLabel}>
                    <input
                      className={Style.inputField}
                      name="signinShowPassword"
                      type="checkbox"
                      value={signinShowPassword}
                      onChange={handleOnChangePasswordInput}
                    />
                    <span />
                  </label>
                  <div className={Style.error}>{passwordErrorText}</div>
                </div>
                <p className={Style.termsText}>
                  {verifyPrivacyPolicyText}{' '}
                  <a
                    target="_blank"
                    rel="noopener noreferrer"
                    className={Style.termsPolicyLink}
                    href={privacyPolicyUrl}
                  >
                    {verifyPrivacyPolicyLinkText}
                  </a>
                </p>
                <button className={Style.btnPrimary} disabled={isVerifyLoading}>
                  {!isVerifyLoading ? verifyText : <Loader buttonLoader />}
                </button>
              </div>
            </form>
            <div className={Style.verifyOptions}>
              <button
                className={Style.btnHollow}
                disabled={isNoAccountLoading}
                onClick={handleNoAccountClick}
              >
                {!isNoAccountLoading ? CTAText : <Loader buttonLoader />}
              </button>
              <ForgotPasswordCTA {...forgotPasswordCTAProps} />
              <SocialLogin
                facebookGenericError={facebookGenericError}
                facebookLoginOptionalText={facebookLoginOptionalText}
                facebookLoginText={facebookLoginText}
                facebookSignupOptionalText={facebookSignupOptionalText}
                facebookSignupText={facebookSignupText}
                onSocialFacebookLogin={onSocialFacebookLogin}
                showFacebookLogin={showFacebookLogin}
              />
            </div>
          </>
        )}
      </div>
    </main>
  );
};

VerifyAccount.defaultProps = {
  children: {},
  facebookGenericError: null,
  facebookLoginOptionalText: null,
  facebookLoginText: null,
  facebookSignupOptionalText: null,
  facebookSignupText: null,
  href: '',
  showFacebookLogin: null,
  showVerifyConfirmation: false,
  signinDropdownIconTheme: {},
  signinEmail: null,
  signinEmailError: null,
  signinGenericError: null,
  signinMigrationDropdownOpen: false,
  signinPassword: null,
  signinPasswordError: null,
  signinShowPassword: false,
};

VerifyAccount.propTypes = {
  bubbleState: PropTypes.func.isRequired,
  children: PropTypes.shape({}),
  ctaProps: PropTypes.shape({}).isRequired,
  facebookGenericError: PropTypes.string,
  facebookLoginOptionalText: PropTypes.string,
  facebookLoginText: PropTypes.string,
  facebookSignupOptionalText: PropTypes.string,
  facebookSignupText: PropTypes.string,
  forgotPasswordCTAProps: PropTypes.shape({}).isRequired,
  headerProps: PropTypes.shape({}).isRequired,
  href: PropTypes.string,
  language: PropTypes.shape({
    global_signInButton: PropTypes.string,
    profileSignin_header: PropTypes.string,
    profileSignin_migrationDropdownText: PropTypes.string,
    profileSignin_migrationText: PropTypes.string,
    profile_emailInputPlaceholder: PropTypes.string,
    profile_genericErrorSignInMessage: PropTypes.string,
    profile_genericInvalidError: PropTypes.string,
    profile_passwordInputPlaceholder: PropTypes.string,
  }).isRequired,
  onFieldChange: PropTypes.func.isRequired,
  onSignin: PropTypes.func.isRequired,
  onSocialFacebookLogin: PropTypes.func.isRequired,
  showVerifyConfirmation: PropTypes.bool,
  showFacebookLogin: PropTypes.bool,
  signinDropdownIconTheme: PropTypes.shape({}),
  signinEmail: PropTypes.string,
  signinEmailError: PropTypes.bool,
  signinGenericError: PropTypes.string,
  signinMigrationDropdownOpen: PropTypes.bool,
  signinPassword: PropTypes.string,
  signinPasswordError: PropTypes.string,
  signinShowPassword: PropTypes.bool,
};

export default VerifyAccount;
